import { useMemo, useState } from 'react';
import { api, ApiError, formatDate, formatDateTime } from '../../lib/api';
import { memberAdminCodes, useAuth } from '../../lib/auth';
import type { AccessRight, Page } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { EmptyState, ErrorNote, Field, Modal, PageHeader, Spinner, StatusBadge } from '../../ui/components';

export function AccessRights() {
  const { persona } = useAuth();
  const roles = persona?.roles ?? [];
  const isDict = roles.includes('SEYX_ADMIN');
  const myMdas = memberAdminCodes(roles);
  // GET /access-rights (routes/access.ts) allows SEYX_ADMIN OR *any*
  // MEMBER_ADMIN:<code> role — not scoped to that member's own rows. That's
  // the real backend behaviour, not a bug this UI should paper over.
  const canSee = isDict || myMdas.length > 0;
  const canRequest = canSee;

  const [status, setStatus] = useState('REQUESTED');
  const [showRequest, setShowRequest] = useState(false);
  const [rejecting, setRejecting] = useState<AccessRight | null>(null);
  const [revoking, setRevoking] = useState<AccessRight | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const query = useMemo(() => {
    const p = new URLSearchParams({ limit: '100' });
    if (status) p.set('status', status);
    return `/api/v1/seyx/access-rights?${p}`;
  }, [status]);
  const listQ = useApiQuery<Page<AccessRight>>(canSee ? query : null);

  const approve = async (r: AccessRight) => {
    setActionError(null);
    try {
      await api.post(`/api/v1/seyx/access-rights/${r.id}/approve`);
      listQ.reload();
    } catch (e) {
      setActionError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Approval failed');
    }
  };

  if (!canSee) {
    return (
      <div>
        <PageHeader title="Access Rights" subtitle="Request &amp; approval queue for cross-MDA data exchange" />
        <EmptyState title="No access" hint="This screen needs SEYX_ADMIN, or a MEMBER_ADMIN:<code> role." />
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Access Rights"
        subtitle="Request & approval queue for cross-MDA data exchange"
        actions={
          canRequest ? (
            <button className="btn-primary" onClick={() => setShowRequest(true)}>
              + Request access
            </button>
          ) : undefined
        }
      />
      {actionError ? (
        <div className="mb-4">
          <ErrorNote message={actionError} />
        </div>
      ) : null}
      <div className="mb-4">
        <select className="field-input max-w-[180px]" value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="REQUESTED">Requested</option>
          <option value="APPROVED">Approved</option>
          <option value="REJECTED">Rejected</option>
          <option value="REVOKED">Revoked</option>
          <option value="">All</option>
        </select>
      </div>
      {listQ.error ? <ErrorNote message={listQ.error} /> : null}
      <div className="card overflow-x-auto">
        {listQ.loading ? (
          <Spinner />
        ) : (listQ.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="Nothing here" />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Consumer</th>
                <th className="table-head">Provider</th>
                <th className="table-head">Service</th>
                <th className="table-head">Purpose</th>
                <th className="table-head">Legal basis</th>
                <th className="table-head">Status</th>
                <th className="table-head">Valid to</th>
                <th className="table-head" />
              </tr>
            </thead>
            <tbody>
              {listQ.data!.items.map((r) => (
                <tr key={r.id} className="hover:bg-slate-50">
                  <td className="table-cell font-mono text-xs">{r.consumerMember}</td>
                  <td className="table-cell font-mono text-xs">{r.providerMember}</td>
                  <td className="table-cell font-mono text-xs">{r.serviceCode}</td>
                  <td className="table-cell max-w-[220px] truncate text-xs" title={r.purpose}>
                    {r.purpose}
                  </td>
                  <td className="table-cell max-w-[180px] truncate text-xs text-slate-500" title={r.legalBasisRef ?? undefined}>
                    {r.legalBasisRef ?? '—'}
                  </td>
                  <td className="table-cell">
                    <StatusBadge status={r.status} />
                    {r.rejectedReason ? <div className="mt-0.5 text-[11px] text-slate-400">{r.rejectedReason}</div> : null}
                  </td>
                  <td className="table-cell text-slate-500">{formatDate(r.validTo)}</td>
                  <td className="table-cell text-right">
                    {isDict && r.status === 'REQUESTED' ? (
                      <div className="flex justify-end gap-1.5">
                        <button className="btn-success !px-2 !py-0.5 text-xs" onClick={() => approve(r)}>
                          Approve
                        </button>
                        <button className="btn-danger !px-2 !py-0.5 text-xs" onClick={() => setRejecting(r)}>
                          Reject
                        </button>
                      </div>
                    ) : isDict && r.status === 'APPROVED' ? (
                      <button className="btn-danger !px-2 !py-0.5 text-xs" onClick={() => setRevoking(r)}>
                        Revoke
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showRequest ? (
        <RequestModal
          isDict={isDict}
          myMdas={myMdas}
          onClose={() => setShowRequest(false)}
          onDone={() => {
            setShowRequest(false);
            listQ.reload();
          }}
        />
      ) : null}
      {rejecting ? (
        <ReasonModal
          title={`Reject access right — ${rejecting.consumerMember} → ${rejecting.providerMember}/${rejecting.serviceCode}`}
          submitLabel="Reject"
          endpoint={`/api/v1/seyx/access-rights/${rejecting.id}/reject`}
          onClose={() => setRejecting(null)}
          onDone={() => {
            setRejecting(null);
            listQ.reload();
          }}
          onError={setActionError}
        />
      ) : null}
      {revoking ? (
        <ReasonModal
          title={`Revoke access right — ${revoking.consumerMember} → ${revoking.providerMember}/${revoking.serviceCode}`}
          submitLabel="Revoke"
          endpoint={`/api/v1/seyx/access-rights/${revoking.id}/revoke`}
          onClose={() => setRevoking(null)}
          onDone={() => {
            setRevoking(null);
            listQ.reload();
          }}
          onError={setActionError}
        />
      ) : null}
    </div>
  );
}

function ReasonModal({
  title,
  submitLabel,
  endpoint,
  onClose,
  onDone,
  onError,
}: {
  title: string;
  submitLabel: string;
  endpoint: string;
  onClose: () => void;
  onDone: () => void;
  onError: (m: string) => void;
}) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post(endpoint, { reason });
      onDone();
    } catch (e) {
      onError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Action failed');
      onClose();
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={title} onClose={onClose}>
      <div className="space-y-3">
        <Field label="Reason">
          <textarea className="field-input" rows={3} value={reason} onChange={(e) => setReason(e.target.value)} />
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-danger" onClick={submit} disabled={busy || reason.length < 3}>
            {busy ? `${submitLabel}…` : submitLabel}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function RequestModal({ isDict, myMdas, onClose, onDone }: { isDict: boolean; myMdas: string[]; onClose: () => void; onDone: () => void }) {
  const [consumerMember, setConsumerMember] = useState(isDict ? '' : (myMdas[0] ?? ''));
  const [providerMember, setProviderMember] = useState('');
  const [serviceCode, setServiceCode] = useState('');
  const [purpose, setPurpose] = useState('');
  const [legalBasisRef, setLegalBasisRef] = useState('');
  const [validMonths, setValidMonths] = useState('12');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/v1/seyx/access-rights', {
        consumerMember,
        providerMember,
        serviceCode,
        purpose,
        legalBasisRef: legalBasisRef || undefined,
        validMonths: Number.parseInt(validMonths, 10) || 12,
      });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Request failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Request access right" onClose={onClose} wide>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <div className="grid grid-cols-3 gap-3">
          <Field label="Consumer MDA (you)">
            {isDict ? (
              <input
                className="field-input font-mono"
                value={consumerMember}
                onChange={(e) => setConsumerMember(e.target.value.toUpperCase())}
                placeholder="IMM"
              />
            ) : myMdas.length > 1 ? (
              <select className="field-input" value={consumerMember} onChange={(e) => setConsumerMember(e.target.value)}>
                {myMdas.map((code) => (
                  <option key={code} value={code}>
                    {code}
                  </option>
                ))}
              </select>
            ) : (
              <input className="field-input font-mono" value={consumerMember} disabled />
            )}
          </Field>
          <Field label="Provider MDA">
            <input
              className="field-input font-mono"
              value={providerMember}
              onChange={(e) => setProviderMember(e.target.value.toUpperCase())}
              placeholder="SRC"
            />
          </Field>
          <Field label="Service code">
            <input className="field-input font-mono" value={serviceCode} onChange={(e) => setServiceCode(e.target.value.toLowerCase())} placeholder="tax-clearance-status" />
          </Field>
        </div>
        <Field label="Purpose (min 10 characters)">
          <textarea className="field-input" rows={2} value={purpose} onChange={(e) => setPurpose(e.target.value)} placeholder="Why this exchange is needed" />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Legal basis reference (C7) — required for PERSONAL/SENSITIVE data before DICT can approve">
            <input className="field-input" value={legalBasisRef} onChange={(e) => setLegalBasisRef(e.target.value)} placeholder="e.g. Immigration Act 2012, s.17" />
          </Field>
          <Field label="Valid for (months)">
            <input className="field-input" type="number" min="1" max="60" value={validMonths} onChange={(e) => setValidMonths(e.target.value)} />
          </Field>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn-primary"
            onClick={submit}
            disabled={busy || !consumerMember || !providerMember || !serviceCode || purpose.length < 10}
          >
            {busy ? 'Requesting…' : 'Request access'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
